import { useCallback, useEffect, useRef, useState } from "react";
import { localStorageManager } from "@/services/localStorage";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";
import { TaskListFilters, TaskStatus } from "@/lib/taskSelectors";

export const EMPTY_FILTERS: TaskListFilters = {
  statuses: [],
  assigneeIds: [],
  showCompleted: false,
};

const VALID_STATUSES = new Set<string>(Object.values(TaskStatus));

/**
 * Builds the scope suffix `localStorageManager` appends to the base key.
 * Composite when both are known (`<shopId>-<userId>`) so the stored filters
 * are per-user as well as per-shop; falls back to `shopId` alone when
 * `userId` isn't available yet, and to `undefined` (unscoped) when neither
 * is — mirroring the optional-`shopId` tolerance the hook already had.
 *
 * The `shopId`-only fallback is unreachable from `TasksTab`, the hook's only
 * caller today: `InboxPanel.tsx`'s `!conversation || !user || !shopId` guard
 * gates the whole `Tabs` tree TasksTab lives in, and `User.id` is a required
 * field, so `shopId` and `userId` are always present together by the time
 * TasksTab calls this hook. The fallback exists purely so the hook degrades
 * gracefully — same key it always used to write — rather than throwing, if
 * it's ever reused from a surface without a resolved user. It never reads a
 * *different* call's key: it keys off the params it was actually called
 * with, so it cannot accidentally rejoin a stale pre-per-user key written by
 * a caller that did have a `userId`.
 */
const buildStorageScope = (
  shopId?: string,
  userId?: string
): string | undefined => (shopId && userId ? `${shopId}-${userId}` : shopId);

/**
 * Rebuilds the stored filters field by field.
 *
 * Deliberately does NOT spread the parsed value over EMPTY_FILTERS: a stored
 * `{"statuses": null}` wins that spread and hands a non-array to filterTasks
 * and to the controlled MultiSelect.
 *
 * Stored assigneeIds are intentionally NOT validated against the live employee
 * list — `employees` loads asynchronously, so a mount-time check would drop
 * valid filters on a race. A stale id simply matches nothing, and the empty
 * state already offers a "Clear filters" escape hatch.
 *
 * One visible consequence of that non-validation: `MultiSelect`'s
 * `displayValue` (`src/components/Base/MultiSelect.tsx:120-130`) maps each
 * selected value to `options.find(...)?.label`, so a stale id with no
 * matching employee option contributes an empty string to the pill text —
 * the assignee pill renders label-less until `employees` finishes loading,
 * and permanently for a deactivated employee. The task list itself stays
 * correct: `filterTasks` compares raw ids and never consults `employees`.
 */
export const readStoredFilters = (
  shopId?: string,
  userId?: string
): TaskListFilters => {
  let parsed: unknown;

  try {
    const stored = localStorageManager.getItem(
      LOCAL_STORAGE_BASE_KEYS.TASK_LIST_FILTERS,
      buildStorageScope(shopId, userId)
    );
    if (!stored) return EMPTY_FILTERS;
    parsed = JSON.parse(stored);
  } catch (error) {
    console.warn("Failed to read task list filters from localStorage:", error);
    return EMPTY_FILTERS;
  }

  if (!parsed || typeof parsed !== "object") return EMPTY_FILTERS;
  const raw = parsed as Record<string, unknown>;

  return {
    statuses: Array.isArray(raw.statuses)
      ? raw.statuses.filter(
          (value): value is TaskStatus =>
            typeof value === "string" && VALID_STATUSES.has(value)
        )
      : [],
    assigneeIds: Array.isArray(raw.assigneeIds)
      ? raw.assigneeIds.filter(
          (value): value is string => typeof value === "string"
        )
      : [],
    showCompleted:
      typeof raw.showCompleted === "boolean" ? raw.showCompleted : false,
  };
};

export interface UsePersistedTaskFiltersResult {
  filters: TaskListFilters;
  /**
   * Updates the filters and mirrors them to localStorage, scoped to the shop
   * and (when known) the user applying them — see `buildStorageScope`.
   */
  setFilters: (next: TaskListFilters) => void;
  /**
   * Applies a one-shot preset (the overdue-task toast) to live state only.
   * Deliberately never written: otherwise one toast click silently becomes the
   * user's saved filters for every future session.
   */
  applyPresetFilters: (next: TaskListFilters) => void;
}

export const usePersistedTaskFilters = (
  shopId?: string,
  userId?: string
): UsePersistedTaskFiltersResult => {
  // Lazy initializer, not an effect — reading in an effect would route the
  // mount path through TasksTab's handleFiltersChange and fire resetSession().
  //
  // This only reads once, on mount. A shop switch CAN change shopId while this
  // subtree stays mounted: setCurrentShopId (shop.slice.ts) never calls
  // unsubscribeShop, so `conversations` still holds the previous shop's data
  // until the new snapshot lands, InboxPanel's `!conversation` guard does not
  // necessarily fire, and the Spinner early-return is not guaranteed. The
  // effect below re-reads for the new shop (or user) when that happens.
  const [filters, setFiltersState] = useState<TaskListFilters>(() =>
    readStoredFilters(shopId, userId)
  );

  // What this ref actually guards: the lazy initializer above has ALREADY
  // read filters for the shopId/userId this hook mounted with. React still
  // runs a newly-created effect once after mount regardless of whether its
  // deps "changed" (there's no prior render to diff against), so a bare
  // `useEffect(..., [shopId, userId])` would immediately re-read on mount —
  // a second, redundant readStoredFilters call producing the same state the
  // initializer already set. These refs record what was already read for, so
  // that first post-mount run is skipped, and the effect only does real work
  // when shopId or userId change on an already-mounted subtree (the shop- or
  // user-switch case described above).
  const lastShopId = useRef(shopId);
  const lastUserId = useRef(userId);
  useEffect(() => {
    if (lastShopId.current === shopId && lastUserId.current === userId) {
      return;
    }
    lastShopId.current = shopId;
    lastUserId.current = userId;
    setFiltersState(readStoredFilters(shopId, userId));
  }, [shopId, userId]);

  const setFilters = useCallback(
    (next: TaskListFilters) => {
      setFiltersState(next);
      try {
        localStorageManager.setItem(
          LOCAL_STORAGE_BASE_KEYS.TASK_LIST_FILTERS,
          JSON.stringify(next),
          buildStorageScope(shopId, userId)
        );
      } catch (error) {
        console.warn(
          "Failed to save task list filters to localStorage:",
          error
        );
      }
    },
    [shopId, userId]
  );

  const applyPresetFilters = useCallback((next: TaskListFilters) => {
    setFiltersState(next);
  }, []);

  return { filters, setFilters, applyPresetFilters };
};
